/**
 * registerSW.js — minimal service-worker bootstrap for the PolkDesQ PWA.
 *
 * - Registers `/sw.js` on `window.load` (waits for first paint to keep
 *   initial render snappy).
 * - Surfaces an `updatefound` notification via the global event bus so the
 *   app can offer a "Reload to update" toast.
 * - Caller may pass `{ onReady, onUpdate }` callbacks.
 */
import { logger } from './utils/logger';

export function registerServiceWorker({ onReady, onUpdate } = {}) {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Skip in development unless explicitly enabled — the CRA dev server
  // pushes HMR updates that conflict with SW caching.
  if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_SW !== '1') return;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      if (registration.active && onReady) onReady(registration);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            if (onUpdate) onUpdate(registration);
          }
        });
      });

      // Periodic update check (every 30min)
      setInterval(() => {
        registration.update().catch(() => {});
      }, 30 * 60 * 1000);
    } catch (err) {
      // SW registration is non-essential; swallow.
      logger.warn('[PolkDesQ SW] registration failed:', err?.message || err);
    }
  });
}

export function unregisterServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
}
